/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState } from "react";
import { Box, Card, CardContent, CircularProgress, IconButton, Stack, Tooltip, Typography } from "@mui/material";

import StyledBreadcrumbs from "../../components/general/StyledBreadcrumbs";
import { useWindowDimensions } from "../../utils/Hooks";
import { Loss, Nullable } from "../../utils/Interfaces";
import DataTable from "../../components/table/DataTable";
import { GridRenderCellParams } from "@mui/x-data-grid";

import { OpenInFull } from "@mui/icons-material";
import LossDetailsModal from "./LossDetailsModal";
import DateIntervalSelector from "../../components/controls/DateIntervalSelector";
import { useMaterialLosses } from "./materialLossesHooks";

const MaterialsLossList = () => {
    const { height } = useWindowDimensions();

    const [selectedLoss, setSelectedLoss] = useState<Nullable<Loss>>(null);
    const [lossDetailsModalOpen, setLossDetailsModalOpen] = useState<boolean>(false);

    const [startDate, setStartDate] = useState<string>();
    const [endDate, setEndDate] = useState<string>();

    const { data, status } = useMaterialLosses(startDate ?? "", endDate ?? "");

    let columns: any[] = [
        {
            field: "view",
            width: 60,
            headerName: "View",
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Tooltip title="View loss details and attachments">
                        <IconButton
                            onClick={() => {
                                setSelectedLoss(params.row);
                                setLossDetailsModalOpen(true);
                            }}
                        >
                            <OpenInFull fontSize="small" />
                        </IconButton>
                    </Tooltip>
                );
            },
        },
        {
            field: "part_material",
            width: 150,
            headerName: "Part/Material",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.name;
            },
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Typography variant="subtitle2" noWrap sx={{ textOverflow: "ellipsis", width: 145 }}>
                        {`${params.row.name}`}
                    </Typography>
                );
            },
        },
        {
            field: "manufacturer",
            width: 150,
            headerName: "Manufacturer",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.manufacturer;
            },
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Typography variant="subtitle2" noWrap sx={{ textOverflow: "ellipsis", width: 145 }}>
                        {`${params.row.manufacturer}`}
                    </Typography>
                );
            },
        },
        {
            field: "part_number",
            width: 150,
            headerName: "Part #",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.part_number;
            },
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Typography variant="subtitle2" noWrap sx={{ textOverflow: "ellipsis", width: 145 }}>
                        {`${params.row.part_number}`}
                    </Typography>
                );
            },
        },
        {
            field: "serial_number",
            width: 150,
            headerName: "Serial #",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.serial_number;
            },
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Typography variant="subtitle2" noWrap sx={{ textOverflow: "ellipsis", width: 145 }}>
                        {`${params.row.serial_number}`}
                    </Typography>
                );
            },
        },
        {
            field: "value",
            width: 100,
            headerName: "Value",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return `$${params.row.price}`;
            },
            renderCell: (params: GridRenderCellParams) => {
                // NOTE: Price is managed on the backend, this is actually `acquisition_price`.
                return (
                    <Typography variant="subtitle2" noWrap sx={{ textOverflow: "ellipsis", width: 95 }}>
                        {`$${params.row.price}`}
                    </Typography>
                );
            },
        },
        {
            field: "date_reported",
            width: 120,
            headerName: "Date Reported",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return new Date(params.row.date_reported).getTime();
            },
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Typography variant="subtitle2" noWrap sx={{ textOverflow: "ellipsis", width: 115 }}>
                        {`${new Date(params.row.date_reported).toLocaleDateString()}`}
                    </Typography>
                );
            },
        },
        {
            field: "responsible",
            width: 150,
            headerName: "Responsible",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return `${params.row.responsible.first_name} ${params.row.responsible.last_name}`;
            },
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Typography variant="subtitle2" noWrap sx={{ textOverflow: "ellipsis", width: 145 }}>
                        {params.row.responsible.first_name} {params.row.responsible.last_name}
                    </Typography>
                );
            },
        },
        {
            field: "reporter",
            width: 200,
            headerName: "Reporter",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return `${params.row.reporter.first_name} ${params.row.reporter.last_name}`;
            },
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Typography variant="subtitle2" noWrap sx={{ textOverflow: "ellipsis", width: 145 }}>
                        {params.row.reporter.first_name} {params.row.reporter.last_name}
                    </Typography>
                );
            },
        },
        {
            field: "notes",
            width: 200,
            headerName: "Notes",
            sortable: true,
            filterable: true,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Typography variant="subtitle2" noWrap sx={{ textOverflow: "ellipsis", width: 195 }}>
                        {params.row.notes}
                    </Typography>
                );
            },
        },
    ];

    return (
        <Box>
            <StyledBreadcrumbs
                paths={[
                    { text: "Techwise Portal", route: "/" },
                    { text: "Materials", route: "/?section=materials" },
                    { text: "Losses", route: "/materials/losses" },
                ]}
            />

            <Box sx={{ mt: 1, mb: 3 }}>
                <DateIntervalSelector
                    onDatesSelected={(startDate: string, endDate: string) => {
                        setStartDate(startDate);
                        setEndDate(endDate);
                    }}
                />
            </Box>

            {status === "pending" && (
                <Stack direction="row" justifyContent="center">
                    <CircularProgress size={40} sx={{ mt: 15, mb: 5 }} />
                </Stack>
            )}

            <LossDetailsModal
                loss={selectedLoss}
                open={lossDetailsModalOpen}
                handleClose={() => setLossDetailsModalOpen(false)}
            />

            {status !== "pending" && (
                <Card sx={{ mt: 1 }}>
                    <CardContent>
                        <DataTable
                            tableName="MATERIALS_LOSSES"
                            data={data?.results ?? []}
                            allowRowSelected={false}
                            columns={columns}
                            height={height - 250}
                        />
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default MaterialsLossList;
